import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { environment } from "app/environments/environment";
import { from, Observable, switchMap } from "rxjs";
import { TokenService } from "./token.service";

@Injectable({
  providedIn: "root",
})
export class AuthInterceptor implements HttpInterceptor {
  private readonly authPaths = ["/auth", "/auth/login", "/login"];
  private readonly skipPathPrefixes = [
    "/assets/",
    "/mock-api/",
  ];

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly tokenService: TokenService,
  ) {}

  private isAuthRequest(url: string): boolean {
    const parsedUrl = this.parseUrl(url);
    if (!parsedUrl || !this.isTrustedHost(parsedUrl.hostname)) {
      return false;
    }

    const pathname = parsedUrl.pathname.toLowerCase();
    return this.authPaths.some(
      (authPath) => pathname === authPath || pathname.startsWith(`${authPath}/`),
    );
  }

  private shouldSkipAuthForRequest(url: string): boolean {
    const parsedUrl = this.parseUrl(url);
    if (!parsedUrl) {
      return true;
    }

    const pathname = parsedUrl.pathname.toLowerCase();
    return this.skipPathPrefixes.some((prefix) => pathname.startsWith(prefix));
  }

  private parseUrl(url: string): URL | null {
    try {
      return new URL(url, window.location.origin);
    } catch {
      return null;
    }
  }

  private isTrustedHost(hostname: string): boolean {
    const trustedHosts = [
      window.location.hostname,
      this.getHostname(environment.apiUrl),
      this.getHostname(environment.monitoringApiUrl),
      this.getHostname(environment.commissionApiUrl),
      this.getHostname(environment.performanceMonitoringUrl),
    ].filter((host): host is string => !!host);

    return trustedHosts.includes(hostname);
  }

  private getHostname(url: string | undefined): string | null {
    if (!url) {
      return null;
    }

    try {
      return new URL(url).hostname;
    } catch {
      return null;
    }
  }

  private addGeneralHeaders(
    request: HttpRequest<any>,
    token: string | null,
  ): HttpRequest<any> {
    let headers = request.headers;
    let languageCode = this.translocoService.getActiveLang();
    if (languageCode === "en-GB") languageCode = "en";
    if (environment.apiKey) {
      headers = headers.set("x-api-key", environment.apiKey);
    }
    headers = headers.set("lan-code", languageCode);
    if (token) {
      headers = headers.set("Authorization", `Bearer ${token}`);
    } else {
      console.warn(
        `AuthInterceptor: No token available for request: ${request.url}`,
      );
    }

    return request.clone({ headers });
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (req.headers.has("X-Public-Battery-Pass")) {
      let headers = req.headers
        .delete("X-Public-Battery-Pass")
        .delete("Authorization");
      let languageCode = this.translocoService.getActiveLang();
      if (languageCode === "en-GB") languageCode = "en";
      if (environment.apiKey) {
        headers = headers.set("x-api-key", environment.apiKey);
      }
      headers = headers.set("lan-code", languageCode);
      return next.handle(req.clone({ headers }));
    }

    if (this.isAuthRequest(req.url) || this.shouldSkipAuthForRequest(req.url)) {
      console.log(`AuthInterceptor: Skipping auth headers for URL: ${req.url}`);
      return next.handle(req);
    }

    return from(this.tokenService.getToken()).pipe(
      switchMap((token) => {
        const modifiedReq = this.addGeneralHeaders(req, token);
        return next.handle(modifiedReq);
      }),
    );
  }
}
